import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  IOWorkflow,
  IoWorkflowBuilderSeedData,
  IoWorkflowBuilderWidgetConfig,
} from './io-workflow-builder.models';

@Injectable({ providedIn: 'root' })
export class IoWorkflowBuilderDataService {
  constructor(private readonly http: HttpClient) {}

  loadSeed(config: IoWorkflowBuilderWidgetConfig): Observable<IoWorkflowBuilderSeedData> {
    const storageKey = config.storageKey || 'io_workflows_chain_dvm';
    const storedWorkflows = this.loadStoredWorkflows(storageKey);
    const seed$ = config.dataFile
      ? this.http.get<IoWorkflowBuilderSeedData>(config.dataFile).pipe(
          catchError(() => of(this.buildInlineSeed(config))),
        )
      : of(this.buildInlineSeed(config));

    return seed$.pipe(
      map((seed) => ({
        ...seed,
        storageKey,
        screenTitle: config.screenTitle ?? seed.screenTitle,
        chainTitle: config.chainTitle ?? seed.chainTitle,
        pipelineLabel: config.pipelineLabel ?? seed.pipelineLabel,
        registry: this.cloneArray(config.registry ?? seed.registry ?? []),
        workflows: storedWorkflows ?? this.cloneWorkflows(config.workflows ?? seed.workflows ?? []),
      })),
    );
  }

  persist(storageKey: string, workflows: IOWorkflow[]): void {
    try {
      localStorage.setItem(storageKey, JSON.stringify(workflows));
    } catch {
      // Ignore persistence failures. The widget remains editable in memory.
    }
  }

  private loadStoredWorkflows(storageKey: string): IOWorkflow[] | null {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;

      const parsed = JSON.parse(raw) as IOWorkflow[];
      const isLegacy = parsed.some((workflow) =>
        workflow.targetMetadata?.some((mapping) => 'operationsList' in (mapping as unknown as Record<string, unknown>)),
      );

      if (isLegacy) {
        localStorage.removeItem(storageKey);
        return null;
      }

      return this.cloneWorkflows(parsed);
    } catch {
      return null;
    }
  }

  private buildInlineSeed(config: IoWorkflowBuilderWidgetConfig): IoWorkflowBuilderSeedData {
    return {
      registry: this.cloneArray(config.registry ?? []),
      workflows: this.cloneWorkflows(config.workflows ?? []),
      screenTitle: config.screenTitle,
      chainTitle: config.chainTitle,
      pipelineLabel: config.pipelineLabel,
      storageKey: config.storageKey,
    };
  }

  private cloneWorkflows(workflows: IOWorkflow[]): IOWorkflow[] {
    return JSON.parse(JSON.stringify(workflows));
  }

  private cloneArray(values: string[]): string[] {
    return [...values];
  }
}
